import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { getSrc } from "gatsby-plugin-image";
import arrow2 from "../../../static/fonts/Arrow2.svg";
import * as styles from "./HomePage.module.scss";
import ReactMarkdown from "react-markdown";

const HomePage = () => {
  const data = useStaticQuery(graphql`
    {
      allMarkdownRemark(filter: { frontmatter: { title: { eq: "HomePage" } } }) {
        edges {
          node {
            frontmatter {
              content {
                type
                windowText
                fullText
                # New universal button fields
                hasOverlay
                buttonLink
                buttonLabel
                # Image fields with multiple fallbacks
                fullImage {
                  publicURL
                  childImageSharp {
                    gatsbyImageData
                  }
                }
                image {
                  publicURL
                  childImageSharp {
                    gatsbyImageData
                  }
                }
                # Product Text fields
                title
                handle
                productDetails
                price
                information
              }
            }
          }
        }
      }
    }
  `);

  if (!data) return null;
  const content = data.allMarkdownRemark.edges[0].node.frontmatter.content;

  // Helper function to get image source with fallbacks
  const getImageSrc = (imageField) => {
    if (!imageField) return null;
    
    // Try Sharp-processed image first
    if (imageField.childImageSharp?.gatsbyImageData) {
      return getSrc(imageField);
    }
    
    // Fallback to publicURL (for GIFs, etc.)
    if (imageField.publicURL) {
      return imageField.publicURL;
    }
    
    // Fallback to raw string path (when editing via CMS before rebuild)
    if (typeof imageField === 'string') {
      return imageField;
    }
    
    return null;
  };

  // Helper function to render overlay button (works for all content types)
  const renderOverlayButton = (section) => {
    // Only show if hasOverlay is true
    if (!section.hasOverlay) {
      return null;
    }

    // Use placeholder text if fields are empty
    const buttonText = section.buttonLabel || "Button text";
    const buttonUrl = section.buttonLink || "#";

    return (
      // In the renderOverlayButton function, change:
      <a 
        href={buttonUrl}
        target={buttonUrl.startsWith('http') ? '_blank' : undefined}
        rel={buttonUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
        className={styles.buttonWrapper}  // ADD THIS LINE
        style={{ textDecoration: 'none', color: 'black' }}
      >
        <div className={styles.shopLinkBtn}>
          <span>{buttonText}</span>
          <img className={styles.arrow} src={arrow2} alt="Arrow" />
        </div>
      </a>
    );
  };

  return (
    <section>
      {content.map((section, index) => {
        switch (section.type) {
          case "fullImage":
            const fullImageSrc = getImageSrc(section.fullImage);
            return (
              <div className={styles.doubleImageWrap} key={fullImageSrc || `fullImage-${index}`}>
                {fullImageSrc && <img className={styles.fullImage} src={fullImageSrc} alt="" />}
                {renderOverlayButton(section)}
              </div>
            );

          case "image":
            const imageSrc = getImageSrc(section.image);
            return (
              <div className={styles.singleImageWrap} key={imageSrc || `image-${index}`}>
                {imageSrc && <img src={imageSrc} alt="" />}
                {renderOverlayButton(section)}
              </div>
            );

          case "windowText":
            // Only add wrapper if there's a button, otherwise keep original structure
            if (section.hasOverlay && section.buttonLink && section.buttonLabel) {
              return (
                <div className={styles.windowText} key={`windowText-${index}`}>
                  <ReactMarkdown>
                    {section.windowText}
                  </ReactMarkdown>
                  {renderOverlayButton(section)}
                </div>
              );
            }
            return (
              <ReactMarkdown key={`windowText-${index}`} className={styles.windowText}>
                {section.windowText}
              </ReactMarkdown>
            );

          case "fullText":
            // Only add wrapper if there's a button, otherwise keep original structure
            if (section.hasOverlay && section.buttonLink && section.buttonLabel) {
              return (
                <div className={styles.fullText} key={`fullText-${index}`}>
                  <ReactMarkdown>
                    {section.fullText}
                  </ReactMarkdown>
                  {renderOverlayButton(section)}
                </div>
              );
            }
            return (
              <ReactMarkdown key={`fullText-${index}`} className={styles.fullText}>
                {section.fullText}
              </ReactMarkdown>
            );

          case "productText":
            return (
              <div 
                className={styles.productText} 
                key={section.handle || `product-${index}`} 
                id={section.handle}
              >
                {section.title && <h1 className={styles.productTitle}>{section.title}</h1>}
                {section.productDetails && <p className={styles.productDetails}>{section.productDetails}</p>}
                {section.price && <p className={styles.productPrice}>{section.price}</p>}
                {section.information && <ReactMarkdown>{section.information}</ReactMarkdown>}
                {renderOverlayButton(section)}
              </div>
            );

          default:
            return <p key={`unknown-${index}`}>Data type not recognized</p>;
        }
      })}
    </section>
  );
};

export default HomePage;